import { useQuery } from '@tanstack/react-query';
import { Image, Video } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useRange } from '@/lib/useRange';
import { RANGE_LABELS, type RangeKey } from '@/lib/range';
import { Card, CardLabel } from '@/components/ui/card';
import { DataTable, type Column } from '@/components/DataTable';
import { TimeSeriesChart } from '@/components/charts/TimeSeriesChart';
import { StackedBarChart } from '@/components/charts/StackedBarChart';
import { Heatmap } from '@/components/charts/Heatmap';
import { Histogram } from '@/components/charts/Histogram';

function api<T>(path: string): () => Promise<T> {
  return () => apiFetch<T>(path);
}

type NotificationType =
  | 'PostLiked'
  | 'Following'
  | 'PostCommented'
  | 'CommentLiked'
  | 'CommentReplied';

interface EventsResponse {
  range: RangeKey;
  types: NotificationType[];
  series: Array<{ date: string } & Record<NotificationType, number>>;
}

interface ActiveUsersResponse {
  range: RangeKey;
  series: { date: string; n: number }[];
}

interface HeatmapResponse {
  range: RangeKey;
  matrix: number[][];
  max: number;
}

interface TopPostOwner {
  uid: string | null;
  username: string | null;
  name: string | null;
  profilePhoto: string | null;
}
interface TopPostRow {
  _id: string;
  caption: string | null;
  post_type: string;
  status: string;
  likes_count: number;
  comments_count: number;
  reposts_count: number;
  createdAt: string;
  thumbnail: string | null;
  owner: TopPostOwner;
}
interface TopPostsResponse {
  range: RangeKey;
  limit: number;
  items: TopPostRow[];
}

interface HistogramResponse {
  range: RangeKey;
  field: string;
  buckets: { label: string; n: number }[];
}

const SERIES_LABELS: Record<NotificationType, string> = {
  PostLiked: 'Post liked',
  Following: 'Followed',
  PostCommented: 'Post commented',
  CommentLiked: 'Comment liked',
  CommentReplied: 'Comment replied',
};

function PostTypeIcon({ type }: { type: string }): JSX.Element {
  if (type === 'clip') return <Video size={14} strokeWidth={1.5} />;
  return <Image size={14} strokeWidth={1.5} />;
}

// DataTable's cell signature passes only the row, so we precompute rank on
// the row and consume it like any other field.
type TopPostRowWithRank = TopPostRow & { rank: number };

const topPostColumnsWithRank: Column<TopPostRowWithRank>[] = [
  {
    key: 'rank',
    header: '#',
    align: 'right',
    width: '50px',
    cell: (r) => (
      <span className="text-fg-subtle font-bold tabular-nums">{r.rank}</span>
    ),
  },
  {
    key: 'preview',
    header: '',
    width: '64px',
    cell: (r) =>
      r.thumbnail ? (
        // Loaded directly from DO Spaces CDN (Malsi already serves them
        // publicly). If the bucket goes private later we'll need a backend
        // signing proxy.
        <img
          src={r.thumbnail}
          alt=""
          className="h-12 w-12 rounded-md object-cover border border-surface-divider bg-surface-raised"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      ) : (
        <div className="h-12 w-12 rounded-md border border-surface-divider bg-surface-raised" />
      ),
  },
  {
    key: 'owner',
    header: 'Owner',
    cell: (r) => (
      <div className="flex items-center gap-2 min-w-0">
        {r.owner.profilePhoto ? (
          <img
            src={r.owner.profilePhoto}
            alt=""
            className="h-7 w-7 rounded-full object-cover border border-surface-divider"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="h-7 w-7 rounded-full bg-surface-raised border border-surface-divider" />
        )}
        <div className="min-w-0">
          <div className="font-medium truncate">
            {r.owner.username ?? r.owner.name ?? '(unknown)'}
          </div>
          {r.owner.username && r.owner.name && (
            <div className="text-xs text-fg-subtle truncate">{r.owner.name}</div>
          )}
        </div>
      </div>
    ),
  },
  {
    key: 'caption',
    header: 'Caption',
    cell: (r) =>
      r.caption?.trim() ? (
        <span className="text-fg-muted">
          {r.caption.length > 64 ? r.caption.slice(0, 64) + '…' : r.caption}
        </span>
      ) : (
        <span className="text-fg-subtle italic">no caption</span>
      ),
  },
  {
    key: 'post_type',
    header: 'Type',
    width: '80px',
    cell: (r) => (
      <span className="inline-flex items-center gap-1 text-xs text-fg-muted">
        <PostTypeIcon type={r.post_type} />
        {r.post_type}
      </span>
    ),
  },
  {
    key: 'likes_count',
    header: 'Likes',
    align: 'right',
    width: '70px',
    cell: (r) => (
      <span className="text-brand-red font-bold tabular-nums">
        {r.likes_count.toLocaleString()}
      </span>
    ),
  },
  {
    key: 'comments_count',
    header: 'Comments',
    align: 'right',
    width: '90px',
    cell: (r) => (
      <span className="tabular-nums">{r.comments_count.toLocaleString()}</span>
    ),
  },
  {
    key: 'createdAt',
    header: 'Posted',
    align: 'right',
    width: '140px',
    cell: (r) =>
      new Date(r.createdAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
  },
];

export function Engagement(): JSX.Element {
  const [range] = useRange();
  const errMsg = (q: { error: unknown }): string | null =>
    q.error ? (q.error as Error).message : null;

  const events = useQuery({
    queryKey: ['metrics', 'engagement', 'events', range],
    queryFn: api<EventsResponse>(
      `/api/metrics/engagement/events?range=${range}`,
    ),
  });
  const active = useQuery({
    queryKey: ['metrics', 'engagement', 'active-users', range],
    queryFn: api<ActiveUsersResponse>(
      `/api/metrics/engagement/active-users?range=${range}`,
    ),
  });
  const heatmap = useQuery({
    queryKey: ['metrics', 'engagement', 'heatmap', range],
    queryFn: api<HeatmapResponse>(
      `/api/metrics/engagement/heatmap?range=${range}`,
    ),
  });
  const topPosts = useQuery({
    queryKey: ['metrics', 'engagement', 'top-liked-posts', range, 10],
    queryFn: api<TopPostsResponse>(
      `/api/metrics/engagement/top-liked-posts?range=${range}&limit=10`,
    ),
  });
  const likesHist = useQuery({
    queryKey: ['metrics', 'engagement', 'likes-histogram', range],
    queryFn: api<HistogramResponse>(
      `/api/metrics/engagement/likes-histogram?range=${range}`,
    ),
  });
  const commentsHist = useQuery({
    queryKey: ['metrics', 'engagement', 'comments-histogram', range],
    queryFn: api<HistogramResponse>(
      `/api/metrics/engagement/comments-histogram?range=${range}`,
    ),
  });

  const rankedTopPosts: TopPostRowWithRank[] = (topPosts.data?.items ?? []).map(
    (r, i) => ({ ...r, rank: i + 1 }),
  );

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="mb-1 text-2xl font-bold">Engagement</h1>
        <p className="text-sm text-fg-muted">
          What activity is happening on Malsi. All charts driven by the range
          picker ({RANGE_LABELS[range]}). Active-users counts are an{' '}
          <span className="text-fg">engagement-based proxy</span> — Malsi has no
          login event log.
        </p>
      </div>

      {/* Stacked events */}
      <Card className="flex flex-col gap-4">
        <div>
          <CardLabel>Engagement events</CardLabel>
          <div className="text-lg font-bold mt-1">
            Daily, stacked by notification type
          </div>
        </div>
        {events.isLoading ? (
          <div className="h-[280px] animate-pulse rounded-md bg-surface-divider/30" />
        ) : events.error ? (
          <div className="text-sm text-state-danger">{errMsg(events)}</div>
        ) : (
          <StackedBarChart
            data={events.data?.series ?? []}
            series={(events.data?.types ?? []).map((t) => ({
              key: t,
              label: SERIES_LABELS[t],
            }))}
          />
        )}
      </Card>

      {/* Active users + heatmap */}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1fr_1.4fr]">
        <Card className="flex flex-col gap-4">
          <div>
            <CardLabel>Active users · daily</CardLabel>
            <div className="text-lg font-bold mt-1">
              Distinct engaging users per day
            </div>
          </div>
          {active.isLoading ? (
            <div className="h-[240px] animate-pulse rounded-md bg-surface-divider/30" />
          ) : active.error ? (
            <div className="text-sm text-state-danger">{errMsg(active)}</div>
          ) : (
            <TimeSeriesChart
              data={active.data?.series ?? []}
              series={[{ key: 'n', label: 'Active users' }]}
            />
          )}
        </Card>

        <Card className="flex flex-col gap-4 overflow-hidden">
          <div>
            <CardLabel>Activity heatmap</CardLabel>
            <div className="text-lg font-bold mt-1">
              Day-of-week × hour (UTC)
            </div>
          </div>
          {heatmap.isLoading ? (
            <div className="h-[200px] animate-pulse rounded-md bg-surface-divider/30" />
          ) : heatmap.error ? (
            <div className="text-sm text-state-danger">{errMsg(heatmap)}</div>
          ) : (
            <Heatmap
              matrix={heatmap.data?.matrix ?? []}
              max={heatmap.data?.max}
            />
          )}
        </Card>
      </div>

      {/* Histograms */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card className="flex flex-col gap-4">
          <div>
            <CardLabel>Likes per post</CardLabel>
            <div className="text-lg font-bold mt-1">
              Distribution across live posts
            </div>
          </div>
          {likesHist.isLoading ? (
            <div className="h-[220px] animate-pulse rounded-md bg-surface-divider/30" />
          ) : likesHist.error ? (
            <div className="text-sm text-state-danger">
              {errMsg(likesHist)}
            </div>
          ) : (
            <Histogram data={likesHist.data?.buckets ?? []} noun="post" />
          )}
        </Card>
        <Card className="flex flex-col gap-4">
          <div>
            <CardLabel>Comments per post</CardLabel>
            <div className="text-lg font-bold mt-1">
              Distribution across live posts
            </div>
          </div>
          {commentsHist.isLoading ? (
            <div className="h-[220px] animate-pulse rounded-md bg-surface-divider/30" />
          ) : commentsHist.error ? (
            <div className="text-sm text-state-danger">
              {errMsg(commentsHist)}
            </div>
          ) : (
            <Histogram data={commentsHist.data?.buckets ?? []} noun="post" />
          )}
        </Card>
      </div>

      {/* Top liked posts */}
      <Card className="flex flex-col gap-4">
        <div>
          <CardLabel>Top liked posts</CardLabel>
          <div className="text-lg font-bold mt-1">
            Top 10 live posts by likes · {RANGE_LABELS[range]}
          </div>
        </div>
        <DataTable
          columns={topPostColumnsWithRank}
          rows={rankedTopPosts}
          loading={topPosts.isLoading}
          error={errMsg(topPosts)}
          rowKey={(r) => r._id}
        />
      </Card>
    </div>
  );
}
